import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Sparkles,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import { Button } from 'src/newSrc/components/UI/button';
import { cn } from 'src/newSrc/components/UI/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/newSrc/components/UI/dialog';

const AUTOPLAY_INTERVAL_MS = 2200;

// Default trigger look matches the frosted-glass pills in the newSrc navbar
// (src/newSrc/components/Navbar) — callers rendered inside a differently
// styled navbar (e.g. the legacy navbar) can override via
// triggerClassName / triggerStyle / triggerIconClassName.
const DEFAULT_TRIGGER_CLASSNAME =
  'tw-group tw-mr-6 tw-flex tw-shrink-0 tw-cursor-pointer tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-3.5 tw-py-1.5 tw-text-sm tw-font-semibold tw-text-blue-700 tw-transition-all tw-duration-200 hover:tw-scale-105 active:tw-scale-[0.98] focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-blue-300 focus-visible:tw-ring-offset-2';

const DEFAULT_TRIGGER_STYLE = {
  background:
    'linear-gradient(135deg, rgba(255,255,255,0.72) 0%, rgba(219,234,254,0.55) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1.5px solid rgba(147,197,253,0.6)',
  boxShadow:
    '0 2px 12px rgba(9,56,200,0.08), 0 1px 3px rgba(15,23,42,0.06), inset 0 1px 0 rgba(255,255,255,0.95)',
};

const DEFAULT_TRIGGER_ICON_CLASSNAME =
  'tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 group-hover:tw-rotate-6 group-hover:tw-scale-110';

const ShowcaseCarousel = ({
  slides,
  ariaLabel,
  activeIndex,
  onPrev,
  onNext,
  onSelect,
  onHoverChange,
}) => {
  return (
    <div
      className='tw-relative tw-h-56 tw-w-full tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-gray-200 tw-bg-gray-50 tw-shadow-sm sm:tw-h-72'
      role='region'
      aria-roledescription='carousel'
      aria-label={ariaLabel}
      onMouseEnter={() => onHoverChange(true)}
      onMouseLeave={() => onHoverChange(false)}
    >
      {slides.map((slide, index) => {
        const SlideIcon = slide.icon;
        return (
          <div
            key={slide.title}
            aria-hidden={index !== activeIndex}
            className={cn(
              'tw-absolute tw-inset-0 tw-h-full tw-w-full tw-animate-float tw-transition-all tw-duration-300 tw-ease-out',
              index === activeIndex
                ? 'tw-scale-100 tw-opacity-100'
                : 'tw-scale-95 tw-opacity-0'
            )}
          >
            {slide.image ? (
              <img
                src={slide.image}
                alt={`${slide.title} preview`}
                loading='lazy'
                decoding='async'
                className='tw-h-full tw-w-full tw-object-cover tw-object-top'
              />
            ) : (
              <div className='tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-bg-white'>
                <div className='tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl tw-bg-primary/10 tw-text-primary'>
                  {SlideIcon && <SlideIcon className='tw-h-6 tw-w-6' />}
                </div>
                <span className='tw-text-sm tw-font-medium tw-text-gray-500'>
                  {slide.title}
                </span>
              </div>
            )}
          </div>
        );
      })}

      <button
        type='button'
        aria-label='Previous screenshot'
        onClick={onPrev}
        className='tw-absolute tw-left-2 tw-top-1/2 tw-flex tw-h-8 tw-w-8 tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/90 tw-text-gray-700 tw-shadow-md tw-transition-all hover:tw-scale-110 hover:tw-bg-white'
      >
        <ChevronLeft className='tw-h-4 tw-w-4' />
      </button>
      <button
        type='button'
        aria-label='Next screenshot'
        onClick={onNext}
        className='tw-absolute tw-right-2 tw-top-1/2 tw-flex tw-h-8 tw-w-8 tw--translate-y-1/2 tw-items-center tw-justify-center tw-rounded-full tw-bg-white/90 tw-text-gray-700 tw-shadow-md tw-transition-all hover:tw-scale-110 hover:tw-bg-white'
      >
        <ChevronRight className='tw-h-4 tw-w-4' />
      </button>

      <div
        className='tw-absolute tw-bottom-2 tw-left-1/2 tw-flex tw--translate-x-1/2 tw-items-center tw-gap-1.5'
        role='tablist'
        aria-label='Screenshot pagination'
      >
        {slides.map((slide, index) => (
          <button
            key={slide.title}
            type='button'
            role='tab'
            aria-selected={index === activeIndex}
            aria-label={`Show ${slide.title} screenshot`}
            onClick={() => onSelect(index)}
            className={cn(
              'tw-h-1.5 tw-rounded-full tw-bg-white/70 tw-transition-all tw-duration-300',
              index === activeIndex
                ? 'tw-w-6 tw-bg-white'
                : 'tw-w-1.5 hover:tw-bg-white/90'
            )}
          />
        ))}
      </div>
    </div>
  );
};

const ExperienceSwitchPrompt = ({
  triggerLabel,
  icon: Icon = Sparkles,
  title,
  subtitle,
  slides,
  carouselAriaLabel,
  showScreenshots = true,
  highlights,
  targetRoute,
  primaryLabel,
  secondaryLabel,
  triggerClassName = DEFAULT_TRIGGER_CLASSNAME,
  triggerStyle = DEFAULT_TRIGGER_STYLE,
  triggerIconClassName = DEFAULT_TRIGGER_ICON_CLASSNAME,
}) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [contentVisible, setContentVisible] = useState(false);
  const [textVisible, setTextVisible] = useState(true);
  const [isTickerPaused, setIsTickerPaused] = useState(false);
  const history = useHistory();
  const textTimeoutRef = useRef(null);

  const goToSlide = index => {
    setActiveIndex((index + slides.length) % slides.length);
  };
  const goToPrev = () => goToSlide(activeIndex - 1);
  const goToNext = () => goToSlide(activeIndex + 1);

  useEffect(() => {
    if (!open || !showScreenshots || isPaused) {
      return undefined;
    }
    const id = setInterval(() => {
      setActiveIndex(current => (current + 1) % slides.length);
    }, AUTOPLAY_INTERVAL_MS);
    return () => clearInterval(id);
  }, [open, showScreenshots, isPaused, slides.length]);

  useEffect(() => {
    setTextVisible(false);
    clearTimeout(textTimeoutRef.current);
    textTimeoutRef.current = setTimeout(() => setTextVisible(true), 150);
    return () => clearTimeout(textTimeoutRef.current);
  }, [activeIndex]);

  useEffect(() => {
    if (!open) {
      setContentVisible(false);
      return undefined;
    }
    const frame = requestAnimationFrame(() => setContentVisible(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const handleOpenChange = nextOpen => {
    if (nextOpen) {
      setActiveIndex(0);
    }
    setOpen(nextOpen);
  };

  const goToTarget = () => {
    setOpen(false);
    history.push(targetRoute);
  };

  const activeSlide = slides[activeIndex];
  const ActiveIcon = activeSlide.icon;

  return (
    <>
      <button
        type='button'
        className={triggerClassName}
        style={triggerStyle}
        onClick={() => handleOpenChange(true)}
      >
        <Icon className={triggerIconClassName} />
        {triggerLabel}
      </button>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className='tw-max-w-2xl'>
          <div
            className={cn(
              'tw-min-w-0 tw-transition-all tw-duration-300 tw-ease-out',
              contentVisible
                ? 'tw-translate-y-0 tw-scale-100 tw-opacity-100'
                : 'tw-translate-y-2 tw-scale-95 tw-opacity-0'
            )}
          >
            <DialogHeader>
              <DialogTitle className='tw-flex tw-items-center tw-gap-2 tw-text-xl'>
                <Icon className='tw-h-5 tw-w-5 tw-text-primary' />
                {title}
              </DialogTitle>
              <DialogDescription>{subtitle}</DialogDescription>
            </DialogHeader>

            <div className='tw-mt-4 tw-space-y-4'>
              {showScreenshots ? (
                <>
                  <ShowcaseCarousel
                    slides={slides}
                    ariaLabel={carouselAriaLabel}
                    activeIndex={activeIndex}
                    onPrev={goToPrev}
                    onNext={goToNext}
                    onSelect={goToSlide}
                    onHoverChange={setIsPaused}
                  />

                  <div
                    aria-live='polite'
                    className={cn(
                      'tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-bg-primary/5 tw-p-3 tw-transition-opacity tw-duration-300',
                      textVisible ? 'tw-opacity-100' : 'tw-opacity-0'
                    )}
                  >
                    <div className='tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-primary'>
                      <ActiveIcon className='tw-h-4 tw-w-4' />
                    </div>
                    <div>
                      <p className='tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-900'>
                        {activeSlide.heading}
                      </p>
                      <p className='tw-m-0 tw-mt-0.5 tw-text-sm tw-text-gray-500'>
                        {activeSlide.description}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className='tw-grid tw-grid-cols-1 tw-gap-2 sm:tw-grid-cols-2'>
                  {slides.map(slide => {
                    const SlideIcon = slide.icon;
                    return (
                      <div
                        key={slide.title}
                        className='tw-flex tw-items-start tw-gap-3 tw-rounded-xl tw-bg-primary/5 tw-p-3'
                      >
                        <div className='tw-flex tw-h-9 tw-w-9 tw-flex-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-primary/10 tw-text-primary'>
                          {SlideIcon && <SlideIcon className='tw-h-4 tw-w-4' />}
                        </div>
                        <div>
                          <p className='tw-m-0 tw-text-sm tw-font-semibold tw-text-gray-900'>
                            {slide.heading}
                          </p>
                          <p className='tw-m-0 tw-mt-0.5 tw-text-sm tw-text-gray-500'>
                            {slide.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div
                className='tw-[mask-image:linear-gradient(90deg,transparent,black_10%,black_90%,transparent)] tw-relative tw-overflow-hidden'
                onMouseEnter={() => setIsTickerPaused(true)}
                onMouseLeave={() => setIsTickerPaused(false)}
              >
                <div
                  className='tw-flex tw-w-max tw-animate-marquee tw-items-center tw-gap-2'
                  style={{
                    animationPlayState: isTickerPaused ? 'paused' : 'running',
                  }}
                >
                  {[...highlights, ...highlights].map((highlight, index) => (
                    <div
                      key={`${highlight}-${index}`}
                      className='tw-flex tw-flex-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-border tw-border-solid tw-border-primary/10 tw-bg-primary/5 tw-px-3 tw-py-1'
                    >
                      <CheckCircle2 className='tw-h-3.5 tw-w-3.5 tw-flex-shrink-0 tw-text-primary' />
                      <span className='tw-whitespace-nowrap tw-text-xs tw-font-medium tw-text-primary'>
                        {highlight}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className='tw-mt-4'>
              <Button variant='defaultOutline' onClick={() => setOpen(false)}>
                {secondaryLabel}
              </Button>
              <Button onClick={goToTarget} className='tw-group'>
                {primaryLabel}
                <ArrowRight className='tw-ml-1.5 tw-h-4 tw-w-4 tw-transition-transform tw-duration-200 group-hover:tw-translate-x-1' />
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ExperienceSwitchPrompt;
